import { AvatarDropdown, Footer, HeaderSearch } from "@/components";
import * as AntdIcons from "@ant-design/icons";
import NotificationBell from "@/components/RightContent/NotificationBell";
import CallbackBadge from "@/components/RightContent/CallbackBadge";
import AutoScheduleBadge from "@/components/RightContent/AutoScheduleBadge";
import UnverifiedAddressBadge from "@/components/RightContent/UnverifiedAddressBadge";
import ConflictBell from "@/components/RightContent/ConflictBell";
import CsrClockWidget from "@/components/RightContent/CsrClockWidget";
import AgentStatusWidget from "@/components/RightContent/AgentStatusWidget";
import ClockInGate from "@/components/ClockInGate";
import { currentUser as queryCurrentUser } from "@/services/app/api";
import type { Settings as LayoutSettings } from "@ant-design/pro-components";
import { SettingDrawer } from "@ant-design/pro-components";
import type { RequestConfig, RunTimeLayoutConfig } from "@umijs/max";
import { history, Link } from "@umijs/max";
import React, { useEffect, useState } from "react";
import defaultSettings from "../config/defaultSettings";
import { errorConfig } from "./requestErrorConfig";
import SAvatar from "./components/SAvatar";
import "@ant-design/v5-patch-for-react-19";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { LS_TOKEN_NAME } from "./constants";
import { getQueryParamInUrl, isPublicRoute, setActiveUserTimezone, setServerTimezone, sInitials } from "./util";

dayjs.extend(utc);
dayjs.extend(timezone);
import { registerTenantPushToken, setupForegroundMessageListener } from "./utils/firebase";
import { consumePushRebind, handleImpersonationAuthFailure, restoreImpersonatorIfEnded, watchImpersonationSwitch } from "./utils/impersonation";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import NotificationManager from "@/components/NotificationManager";
import CsrActivityTracker from "@/components/CsrActivityTracker";

import ErrorBoundary from "@/components/ErrorBoundary";
import AiRescheduleBanner from "@/components/AiRescheduleBanner";
import CallbackQueueBanner from "@/components/CallbackQueueBanner";
import CallbackPostCallPrompt from "@/components/CallbackQueue/CallbackPostCallPrompt";
import { TwilioDeviceProvider, CallWidget } from "@/components/CallWidget";
import { ChatProvider, ChatWidget, ChatBell } from "@/components/Chat";
import { AudioPlayerProvider, AudioPlayerWidget } from "@/components/AudioPlayer";
import { CallbackQueueProvider } from "@/contexts/CallbackQueueContext";
import NoAccessPage from "@/pages/403";
import { PERMISSIONS } from "./constants/permissions";
const isDev = process.env.NODE_ENV === "development";
const loginPath = "/user/login";

/**
 * Strip the base path (PUBLIC_PATH) from a URL path before passing to history.push().
 * history.push() in umi automatically prepends the base, but redirect params captured from
 * window.location.pathname already include it — which would produce a double base like /v2/v2/...
 * Falls back to EDB if no valid path is provided.
 */
const toSpaPath = (path: string | null | undefined): string => {
    if (!path) return '/tenant/dashboard/edb';
    const base = PUBLIC_PATH.endsWith('/') ? PUBLIC_PATH.slice(0, -1) : PUBLIC_PATH;
    return path.startsWith(base) ? (path.slice(base.length) || '/') : path;
};

dayjs.locale("en-US");
localStorage.setItem("umi_locale", "en-US");
watchImpersonationSwitch();

/**
 * Umi's generated plugin-layout/runtime.tsx `patchRoutes` hook already walks every route
 * (top-level AND nested) and turns a string `icon` into a real <Icon /> element before
 * ProLayout ever sees it — so `item.icon` here is normally already a ReactElement, not a
 * string. ProLayout's own defaultDom knows how to render that element directly for items
 * with children at the top level; menuItemRender/subMenuItemRender below fully replace
 * defaultDom, so they need to handle the already-built element themselves (the string branch
 * is kept only as a defensive fallback in case that changes).
 */
function resolveMenuIcon(icon: unknown): React.ReactNode {
  if (!icon) return null;
  if (React.isValidElement(icon)) return icon;
  if (typeof icon !== "string") return null;
  const hasSuffix = /(Outlined|Filled|TwoTone)$/.test(icon);
  const componentName = hasSuffix ? icon : `${icon.charAt(0).toUpperCase()}${icon.slice(1)}Outlined`;
  const IconComponent = (AntdIcons as unknown as Record<string, React.ComponentType>)[componentName];
  return IconComponent ? <IconComponent /> : null;
}

// ProLayout's own icon wrapper (`.ant-pro-base-menu-item-icon`) adds the gap between icon and
// text; since menuItemRender/subMenuItemRender fully replace defaultDom, that wrapper — and its
// spacing — is gone too, so it's reapplied here explicitly.
const menuIconWrapperStyle: React.CSSProperties = { marginInlineEnd: 8 };

// AntdIcons render as an SVG sized 1em square. Items with no icon still need to reserve that
// same square (invisible) so their label lines up with sibling items that do have one — otherwise
// icon-less entries sit flush left while the rest are indented by the icon's width.
const menuIconPlaceholderStyle: React.CSSProperties = { display: "inline-block", width: "1em" };

function renderMenuIconSlot(icon: unknown): React.ReactNode {
  const iconNode = resolveMenuIcon(icon);
  return <span style={menuIconWrapperStyle}>{iconNode ?? <span style={menuIconPlaceholderStyle} />}</span>;
}

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  // If it is not a login page, execute
  const { location } = history;

  const fetchUserInfo = async () => {
    try {
      // The currentUser API now includes permissions via relation loading
      const user = await queryCurrentUser({
        skipErrorHandler: true,
      });

      return user;
    } catch (error) {
      // A Log In As token that stopped working ends that session — back to the admin, not the login page.
      if (handleImpersonationAuthFailure()) {
        return undefined;
      }

      // Prevent redirect loop if already on login page — use window.location (always current)
      // rather than the closure-captured `location` which reflects the path at app startup.
      if (window.location.pathname.includes(loginPath)) {
        return undefined;
      }

      const redirectUrl = getQueryParamInUrl(window.location.href, "redirect") || window.location.pathname;

      // Use includes() because the path may have a PUBLIC_PATH prefix (e.g. /development_team/v2/user/login)
      if (!redirectUrl || redirectUrl.includes(loginPath)) {
        history.push(`${loginPath}`);
      } else {
        history.push(`${loginPath}?redirect=${encodeURIComponent(window.location.pathname || "")}`);
      }
    }
    return undefined;
  };

  // A Log In As session that ran out while the app was closed: switch back to the admin first.
  if (restoreImpersonatorIfEnded()) {
    return { fetchUserInfo, settings: defaultSettings as Partial<LayoutSettings> };
  }

  let currentUser: API.CurrentUser | undefined;
  if (!isPublicRoute(location.pathname)) {
    currentUser = await fetchUserInfo();
    if (currentUser?.api_token) {
      localStorage.setItem(LS_TOKEN_NAME, currentUser?.api_token || "");
    }
    if (currentUser) {
      if (currentUser.server_timezone) setServerTimezone(currentUser.server_timezone);
      setActiveUserTimezone(
        currentUser.user_profile?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
      );
    }

    // Check permissions and redirect tenant users without job.manage to call_board
    if (currentUser) {
      const userType = currentUser.user_type;
      const permissions = currentUser.permissions || [];
      const isSuperAdmin = currentUser.is_super_admin || false;
      const hasJobManagePermission = permissions.includes(PERMISSIONS.JOB_MANAGE);
      const isTenantWithoutJobManage = userType === "tenant" && !isSuperAdmin && !hasJobManagePermission;

      if (isTenantWithoutJobManage) {
        const isDashboardRoute =
          location.pathname === "/" ||
          location.pathname.endsWith("/v2") ||
          location.pathname.endsWith("/v2/") ||
          location.pathname.includes("/tenant/dashboard");

        if (isDashboardRoute && !location.pathname.includes("/call_board")) {
          history.replace("/tenant/communications/call_board");
        }
      }
    }
  }

  // Auto-setup Firebase foreground message listener if user has notifications enabled
  if (currentUser && typeof window !== "undefined") {
    const existingToken = localStorage.getItem("fcm_token");
    if (existingToken && Notification.permission === "granted") {
      console.log("🔔 Auto-initializing Firebase foreground message listener...");
      setupForegroundMessageListener();
      // Just switched user via Log In As: move this browser's push token to
      // whoever is signed in now, or it keeps getting the previous user's pushes.
      if (consumePushRebind()) {
        registerTenantPushToken(existingToken).catch(() => undefined);
      }
    }
  }

  if (location.pathname.includes(loginPath) && currentUser) {
    const urlParams = new URL(window.location.href).searchParams;
    const redirect = urlParams.get("redirect");

    // Use includes() because the path may have a PUBLIC_PATH prefix (e.g. /development_team/v2/user/login)
    if (!redirect || redirect.includes(loginPath)) {
      history.push('/tenant/dashboard/edb');
    } else {
      // toSpaPath strips the base path (e.g. /v2/) so history.push doesn't double-apply it
      history.push(toSpaPath(redirect));
    }
  }

  return {
    fetchUserInfo,
    currentUser,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// APIs supported by ProLayout https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  return {
    actionsRender: () => [<CsrClockWidget key="clock" />, <AgentStatusWidget key="agent-status" />, <CallbackBadge key="callbacks" />, <AutoScheduleBadge key="auto-schedule" />, <UnverifiedAddressBadge key="unverified-addresses" />, <ConflictBell key="conflicts" />, <ChatBell key="chat" />, <NotificationBell key="notifications" />, <HeaderSearch key="search" />],
    avatarProps: {
      src: initialState?.currentUser?.profile_image,
      title: (
        <>
          {/* Initials circle only when there is no photo — ProLayout already renders the src avatar */}
          {!initialState?.currentUser?.profile_image && (
            <SAvatar size="small" text={initialState?.currentUser?.user_name ?? ""} alt={initialState?.currentUser?.user_name} />
          )}
          <span className={`anticon`} style={{ marginLeft: 4 }}>
            {sInitials(initialState?.currentUser?.user_name)}
          </span>
        </>
      ),
      render: (_, avatarChildren) => {
        return <AvatarDropdown menu={!!initialState?.currentUser}>{avatarChildren}</AvatarDropdown>;
      },
    },
    // footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;

      // If not logged in, redirect to login (except public routes)
      if (!initialState?.currentUser && !isPublicRoute(location.pathname) && !location.pathname.includes(loginPath)) {
        history.push(loginPath);
      }

      // Permission-based routing for tenant users
      if (initialState?.currentUser) {
        const userType = initialState.currentUser.user_type;
        const permissions = initialState.currentUser.permissions || [];
        const isSuperAdmin = initialState.currentUser.is_super_admin || false;
        const hasJobManagePermission = permissions.includes(PERMISSIONS.JOB_MANAGE);
        const isTenantWithoutJobManage = userType === "tenant" && !isSuperAdmin && !hasJobManagePermission;

        // Handle redirect from login page
        if (location.pathname.includes(loginPath)) {
          const urlParams = new URL(window.location.href).searchParams;
          const redirect = urlParams.get("redirect");

          if (isTenantWithoutJobManage) {
            history.push("/tenant/communications/call_board");
          } else {
            // toSpaPath strips the base path (e.g. /v2/) so history.push doesn't double-apply it
            history.push(toSpaPath(redirect));
          }
          return;
        }

        // Prevent tenant users without job.manage from accessing dashboard
        if (isTenantWithoutJobManage) {
          // Block root, /v2, and dashboard routes, but allow call_board
          const shouldRedirect =
            location.pathname === "/" ||
            location.pathname.endsWith("/v2") ||
            location.pathname.endsWith("/v2/") ||
            location.pathname.includes("/tenant/dashboard");

          if (shouldRedirect && !location.pathname.includes("/call_board")) {
            history.push("/tenant/communications/call_board");
          }
        }
      }
    },
    menuHeaderRender: undefined,
    // Custom 403 page
    unAccessible: <NoAccessPage />,

    breadcrumbRender: () => undefined,

    menuItemRender(item, defaultDom, menuProps) {
      // console.log('Menu', item);
      if (item.children?.length) {
        return defaultDom;
      } else {
        const iconSlot = renderMenuIconSlot(item.icon);
        return item.external ? (
          <a href={`${API_URL}${item.path}`}>
            {iconSlot}
            {item.name}
          </a>
        ) : (
          <Link to={item.path || "#"}>
            {iconSlot}
            {item.name}
          </Link>
        );
      }
    },

    // Group/parent menu items (anything with children — e.g. "Customer", "GPS", "Settings")
    // go through this hook, not menuItemRender. ProLayout's BaseMenu only auto-shows icons at
    // the top level (shouldHasIcon = level === 0 || (isGroup && level === 1) — and isGroup is
    // always false here because layout is "top"), so nested group icons silently drop even
    // though `icon` is set. Rather than relying on `item.pro_layout_parentKeys` to detect depth
    // (fragile — it didn't survive access filtering), just render icon + name from scratch at
    // every level, the same way menuItemRender does for leaf items below.
    subMenuItemRender(item) {
      return (
        <>
          {renderMenuIconSlot(item.icon)}
          {item.name}
        </>
      );
    },

    // Add a loading state
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <TwilioDeviceProvider>
          <NotificationManager>
            {/* Route children must be wrapped in their own boundary BELOW
                TwilioDeviceProvider: ProLayout's built-in ErrorBoundary sits above
                childrenRender, so an uncaught page crash would unmount the provider,
                whose cleanup disconnects all active/held Twilio calls. */}
            {/* Site-wide banners share ONE sticky wrapper: two separate sticky elements at
                top:0 would overlap each other. Log In As first (who you are acting as), then
                callbacks (more urgent). Each banner renders null when it has nothing to show,
                so the wrapper collapses to 0px. */}
            <div style={{ position: "sticky", top: 0, zIndex: 100 }}>
              <ImpersonationBanner />
              <CallbackQueueBanner />
              <AiRescheduleBanner />
            </div>
            {/* Modal (portal) — asks the CSR how a callback-queue call went once it ends. */}
            <CallbackPostCallPrompt />
            {/* ChatWidget wraps (rather than sits beside) the page content because
                sidebar mode reflows the page into a [content][sidebar] row — widget
                mode renders children untouched and only adds a floating sibling. */}
            {/* AudioPlayerProvider wraps the page content so any page can start a
                recording, and the widget sits beside it (bottom-left) — outside
                the page ErrorBoundary and never remounted on navigation, so audio
                keeps playing while the user moves around the app. */}
            {/* ClockInGate replaces the page with a full-screen lock for
                clockin.required users who are clocked out. It sits inside
                TwilioDeviceProvider so an auto-close mid-call doesn't drop the call. */}
            <ClockInGate>
              <AudioPlayerProvider>
                <ChatWidget>
                  <ErrorBoundary>{children}</ErrorBoundary>
                </ChatWidget>
                <AudioPlayerWidget />
              </AudioPlayerProvider>
            </ClockInGate>
            <CallWidget />
            <CsrActivityTracker />
            {isDev && (
              <SettingDrawer
                disableUrlParams
                enableDarkTheme
                settings={initialState?.settings}
                onSettingChange={(settings) => {
                  setInitialState((preInitialState) => ({
                    ...preInitialState,
                    settings,
                  }));
                }}
              />
            )}
          </NotificationManager>
        </TwilioDeviceProvider>
      );
    },
    ...initialState?.settings,
    logo: PUBLIC_PATH + "logo.png",
  };
};

/**
 * ChatProvider bootstraps by calling the Zulip-backed chat API as soon as it
 * mounts (identity lookup, presence ping, the long-poll event queue) — all
 * authenticated calls that have no business firing for an anonymous visitor
 * on a public, token-based page (survey form / tracking page). ChatWidget
 * and ChatBell already never render there (those routes use `layout: false`
 * and skip childrenRender/actionsRender entirely), but ChatProvider itself
 * sits above routing in rootContainer, so it still has to be gated
 * explicitly using the same isPublicRoute check used everywhere else public
 * routes need to opt out of authenticated-only behavior.
 */
const ChatProviderGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // useLocation() requires Router context, which isn't available yet at this
  // point in rootContainer — track pathname via the standalone `history`
  // singleton instead (same pattern as TwilioDeviceContext's history.listen).
  const [pathname, setPathname] = useState(() => window.location.pathname);
  useEffect(() => history.listen(({ location }) => setPathname(location.pathname)), []);
  if (isPublicRoute(pathname)) return <>{children}</>;
  return <ChatProvider>{children}</ChatProvider>;
};

/**
 * CallbackQueueProvider owns the single shared polling loop for the callback
 * queue. It is mounted here (rootContainer) — above all UmiJS providers —
 * so that both actionsRender (CallbackBadge) and childrenRender (CallQueuePanel)
 * share the same context. Safe at root level because it never calls useModel.
 *
 * ChatProvider is mounted here for the same reason: actionsRender (ChatBell)
 * and childrenRender (ChatWidget) are siblings under the layout config, not
 * nested, so a provider placed inside childrenRender (like TwilioDeviceProvider)
 * would never reach ChatBell.
 */
export function rootContainer(container: React.ReactElement) {
  return (
    <CallbackQueueProvider>
      <ChatProviderGate>{container}</ChatProviderGate>
    </CallbackQueueProvider>
  );
}

/**
 * @name request configuration, you can configure error handling
 * It provides a unified network request and error handling solution based on useRequest of axios and ahooks.
 * @doc https://umijs.org/docs/max/request#Configuration
 */
export const request: RequestConfig = {
  baseURL: API_URL,
  ...errorConfig,
};
